import { createAsyncThunk } from '@reduxjs/toolkit'
import type { RootState } from 'store/index.ts'
import { stringify } from 'qs'
import { Methods, Response } from 'types/request.ts'
import { ApiGetContactsByEmailResponse, ApiGetMyContactResponse } from 'types/contacts.ts'
import request, { getAxiosRequestConfig, newRequest } from 'utils/request.ts'
import {
  GetEmailByContactIdPayload,
  GetEmailByContactIdResponse,
  GetContactErrorPayload,
  GetContactPayload,
  GetContactSuccessPayload,
  RejectValue,
  ThunkDefaultErrorMsg,
  UpdateBillingPayload,
  UpdateBillingSuccessPayload,
  UpdateContactPayload,
  UpdateContactResponse,
  UpdateInstalmentsPayload
} from 'store/contact/thunks/types.ts'
import { UrlSearchParamsKeys } from 'store/app/types.ts'
import { switchLanguage } from 'store/app/thunks'
import { ignoreGasOnlyContracts, processContact } from './utils.ts'
import { UpdateAccountPayload, UpdateAccountResponse } from 'store/queries/bolt-api/contacts/types.ts'
import { boltApi } from 'store/queries/bolt-api'
import i18next from 'i18next'
import { setPersistedItem } from 'utils/persistedStorage.ts'
import { LANGUAGE_STORAGE_KEY } from 'constants/constants.ts'
import { popErrorToast, popSuccessToast } from '@boltenergy-be/design-system'
import { saveUrlSearchParam } from 'store/app/slice.ts'
import { Language } from '@boltenergy-be/shared'
import { log } from 'utils/logging.ts'
import { isResponseCode } from 'utils/errors.ts'
import { Flow } from 'types/logging.ts'
import { BOLT_BACKEND_TAGS } from 'store/queries/bolt-backend/constants.ts'
import { getLowerCaseLanguage } from 'utils/language.ts'

export const getMyContact = createAsyncThunk<GetContactSuccessPayload, void, { rejectValue: GetContactErrorPayload | string }>(
  'contact/getMyContact',
  async (_, thunkAPI) => {
    try {
      const { success, data, message, error, errorCode }: Response<ApiGetMyContactResponse> = await newRequest(
        getAxiosRequestConfig(Methods.GET, '/contacts/me')
      )

      if (success && data) {
        // Persist language
        setPersistedItem(LANGUAGE_STORAGE_KEY, getLowerCaseLanguage(data.contact.language), 365)

        // Fetch current language and change it if it doesn't match the user language
        const lang: Language = (thunkAPI.getState() as RootState).app.language
        if (lang !== data.contact.language.toUpperCase()) void thunkAPI.dispatch(switchLanguage({ language: data.contact.language }))

        // Ignore gas only contracts
        const { ignoredBillingContracts, updatedContact } = ignoreGasOnlyContracts(data.contact)

        const persistedSelected = (thunkAPI.getState() as RootState).contact.selected
        const processedContactData = processContact(updatedContact || data.contact, persistedSelected)

        // Clear the url search param
        thunkAPI.dispatch(saveUrlSearchParam({ key: UrlSearchParamsKeys.CUSTOMER_ID, value: '' }))
        thunkAPI.dispatch(saveUrlSearchParam({ key: UrlSearchParamsKeys.BILLING_CONTRACT_NUMBER, value: '' }))

        return {
          ignoredBillingContracts,
          ...processedContactData
        }
      }

      const responseCode = [errorCode, message].find(isResponseCode)
      log({
        error: message || 'Failed to fetch contact data',
        identifier: `[${Flow.CONTACTS}:getMyContact${responseCode ? `:${responseCode}` : ''}]`,
        errorCode: responseCode
      })

      return thunkAPI.rejectWithValue({
        error: message || error || ThunkDefaultErrorMsg.GET_MY_CONTACT
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      log({
        error: error instanceof Error ? error : 'Failed to fetch contact data',
        identifier: `[${Flow.CONTACTS}:CATCH:getMyContact]`
      })
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const getContactByEmail = createAsyncThunk<
  GetContactSuccessPayload,
  GetContactPayload,
  {
    rejectValue: GetContactErrorPayload
  }
>('contact/getContactByEmail', async ({ email }, thunkAPI) => {
  try {
    const query = stringify({ email, customers: true }, { skipNulls: true, addQueryPrefix: true })
    const { success, data, message, error, errorData }: Response<ApiGetContactsByEmailResponse> = await newRequest(
      getAxiosRequestConfig(Methods.GET, `/contacts${query}`)
    )

    if (success && !!data?.contacts?.length) {
      const contact = data.contacts[0]

      // Persist language
      setPersistedItem(LANGUAGE_STORAGE_KEY, getLowerCaseLanguage(contact.language), 365)

      // Fetch current language and change it if it doesn't match the user language
      const lang: Language = (thunkAPI.getState() as RootState).app.language
      if (lang !== contact.language.toUpperCase()) void thunkAPI.dispatch(switchLanguage({ language: contact.language }))

      // Ignore gas only contracts
      const { ignoredBillingContracts, updatedContact } = ignoreGasOnlyContracts(contact)
      const processedContactData = processContact(updatedContact || contact)

      // Clear the url search param
      thunkAPI.dispatch(saveUrlSearchParam({ key: UrlSearchParamsKeys.CUSTOMER_ID, value: '' }))
      thunkAPI.dispatch(saveUrlSearchParam({ key: UrlSearchParamsKeys.BILLING_CONTRACT_NUMBER, value: '' }))

      return {
        ignoredBillingContracts,
        ...processedContactData
      }
    }

    return thunkAPI.rejectWithValue({
      error: message || error || ThunkDefaultErrorMsg.GET_CONTACT_BY_EMAIL,
      ...(errorData && { errorData })
    })
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error'
    return thunkAPI.rejectWithValue({
      error: message
    })
  }
})

// UPDATE BILLING CONTRACT
export const updateBillingContract = createAsyncThunk<UpdateBillingSuccessPayload, UpdateBillingPayload, RejectValue>(
  'contact/updateBillingContract',
  async ({ billingContractId, updateBillingContractData }, thunkAPI) => {
    try {
      const { success, data, message }: Response<UpdateBillingSuccessPayload> = await newRequest(
        getAxiosRequestConfig(Methods.PATCH, `/contracts/${billingContractId}`, { contract: updateBillingContractData })
      )

      if (success && data) {
        return data
      } else {
        return thunkAPI.rejectWithValue(message || ThunkDefaultErrorMsg.UPDATE_BILLING_CONTRACT)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      return thunkAPI.rejectWithValue(message)
    }
  }
)

// UPDATE CONTACT
export const updateContact = createAsyncThunk<UpdateContactResponse, UpdateContactPayload, RejectValue>(
  'contact/updateContact',
  async ({ contactId, contact, deliveryAddress }, thunkAPI) => {
    try {
      const { success, data, message } = await newRequest<UpdateContactResponse>(
        getAxiosRequestConfig(Methods.PATCH, `/contacts/${contactId}`, { contact, deliveryAddress })
      )

      if (success && data) {
        return data
      } else {
        return thunkAPI.rejectWithValue(message || ThunkDefaultErrorMsg.UPDATE_CONTACT)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      return thunkAPI.rejectWithValue(message)
    }
  }
)

// UPDATE ACCOUNT
export const updateAccount = createAsyncThunk<UpdateAccountResponse, UpdateAccountPayload, RejectValue>(
  'contact/updateAccount',
  async ({ contactId, accountId, account, deliveryAddress }, thunkAPI) => {
    try {
      const { success, data, message } = await newRequest<UpdateAccountResponse>(
        getAxiosRequestConfig(Methods.PATCH, `/contacts/${contactId}/accounts/${accountId}`, { account, deliveryAddress })
      )

      if (success && data) {
        return data
      } else {
        return thunkAPI.rejectWithValue(message || ThunkDefaultErrorMsg.UPDATE_ACCOUNT)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      return thunkAPI.rejectWithValue(message)
    }
  }
)

// UPDATE INSTALMENT
export const updateInstalment = createAsyncThunk<UpdateInstalmentsPayload, UpdateInstalmentsPayload, RejectValue>(
  'contact/updateInstalment',
  async ({ billingContractId, customerNumber, electricity, gas }, thunkAPI) => {
    try {
      const { success, message }: Response = await newRequest(
        getAxiosRequestConfig(Methods.PATCH, `/contracts/${billingContractId}/instalment`, {
          electricityInstalment: electricity.detail.instalment,
          gasInstalment: gas?.detail.instalment
        })
      )

      if (success) {
        thunkAPI.dispatch(boltApi.util.invalidateTags([BOLT_BACKEND_TAGS.billShock]))
        popSuccessToast(
          i18next.t(($) => $.editInstalment.form.success, {
            ns: 'billing'
          })
        )
        return { billingContractId, electricity, gas, customerNumber }
      } else {
        popErrorToast(
          i18next.t(($) => $.editInstalment.form.error, {
            ns: 'billing'
          })
        )
        return thunkAPI.rejectWithValue(message || ThunkDefaultErrorMsg.UPDATE_INSTALMENT)
      }
    } catch (error) {
      popErrorToast(
        i18next.t(($) => $.editInstalment.form.error, {
          ns: 'billing'
        })
      )
      const message = error instanceof Error ? error.message : 'Unknown error'
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const getEmailByContactId = createAsyncThunk<
  GetEmailByContactIdResponse,
  GetEmailByContactIdPayload,
  {
    rejectValue: string
  }
>('contact/getEmailByContactId', async ({ contactId }, thunkAPI) => {
  try {
    const { success, data, message } = await request<GetEmailByContactIdResponse, string>(
      getAxiosRequestConfig(Methods.GET, `/contact/${contactId}/email`)
    )

    if (success && data) {
      return data
    }

    return thunkAPI.rejectWithValue(message || 'Unknown error')
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error'
    return thunkAPI.rejectWithValue(message)
  }
})
